import React from 'react';
import { Trophy } from 'lucide-react';
import proService, { ProLiveGame, ProParticipant } from '../../services/proService';
import PlayerAvatar from './PlayerAvatar';

const ACCENT = '#3fced1';

/**
 * Prominente Live-Sektion (nur "Heute"): Grid aus Live-Karten über die volle Breite.
 * Rendert nichts, wenn keine Live-Spiele vorhanden sind.
 */
const LiveGamesSection: React.FC<{ games: ProLiveGame[] }> = ({ games }) => {
  if (!Array.isArray(games) || games.length === 0) return null;

  return (
    <section aria-label="Live-Spiele">
      {/* Section-Header mit pulsierendem Live-Punkt */}
      <div className="flex items-center gap-3 mb-5">
        <div className="p-2.5 rounded-xl border border-red-500/30 bg-red-500/10 flex-shrink-0 flex items-center justify-center">
          <span className="relative flex h-[18px] w-[18px] items-center justify-center">
            <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-red-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg md:text-xl font-bold text-white">Jetzt live</h2>
          <p className="text-xs md:text-sm text-gray-500">
            {games.length === 1
              ? 'Ein Spiel läuft gerade'
              : `${games.length} Spiele laufen gerade`}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {games.map((game, i) => (
          <LiveGameCard key={proService.getEventId(game) || `live-${i}`} game={game} />
        ))}
      </div>
    </section>
  );
};

const LiveGameCard: React.FC<{ game: ProLiveGame }> = ({ game }) => {
  const homeName = proService.getParticipantName(game.homeParticipant);
  const awayName = proService.getParticipantName(game.awayParticipant);
  const homeScore = game.liveData?.homeScore?.current ?? proService.getHomeScore(game) ?? 0;
  const awayScore = game.liveData?.awayScore?.current ?? proService.getAwayScore(game) ?? 0;
  const homeAvg = game.liveData?.homeAverage;
  const awayAvg = game.liveData?.awayAverage;
  const service = game.liveData?.service; // 1 = home, 2 = away

  return (
    <div className="rounded-2xl border border-red-500/30 bg-zinc-900/60 p-4 transition-all duration-300 hover:-translate-y-1 hover:border-red-500/50 hover:shadow-[0_12px_32px_-8px_rgba(239,68,68,0.25)]">
      {/* Turnier + LIVE-Badge */}
      <div className="flex items-center justify-between gap-3 mb-3.5">
        <div className="flex items-center gap-2 min-w-0">
          <Trophy size={13} color={ACCENT} className="flex-shrink-0" />
          <span className="text-xs text-gray-400 truncate">
            {proService.getTournamentName(game.tournament)}
          </span>
        </div>
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/30 flex-shrink-0">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
          </span>
          <span className="text-[10px] font-bold text-red-400 tracking-wider">LIVE</span>
        </span>
      </div>

      <LivePlayerRow
        participant={game.homeParticipant}
        name={homeName}
        score={homeScore}
        avg={homeAvg}
        serving={service === 1}
      />
      <LivePlayerRow
        participant={game.awayParticipant}
        name={awayName}
        score={awayScore}
        avg={awayAvg}
        serving={service === 2}
      />
    </div>
  );
};

const LivePlayerRow: React.FC<{
  participant?: ProParticipant;
  name: string;
  score: number;
  avg?: string | number;
  serving?: boolean;
}> = ({ participant, name, score, avg, serving }) => {
  const hasAvg =
    avg !== undefined && avg !== null && String(avg) !== '' && String(avg) !== '0.00';

  return (
    <div className="flex items-center gap-2.5 py-1.5">
      <PlayerAvatar participant={participant} size="md" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm font-semibold text-white truncate">{name}</span>
          {serving && (
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0 animate-pulse"
              style={{ backgroundColor: ACCENT }}
              title="Am Wurf"
              aria-label="Am Wurf"
            />
          )}
        </div>
        {hasAvg && (
          <span className="text-[11px] text-gray-500 tabular-nums">Ø {String(avg)}</span>
        )}
      </div>
      <span className="text-2xl font-bold text-white tabular-nums flex-shrink-0">{score}</span>
    </div>
  );
};

export default LiveGamesSection;
